import { type IntlShape, type MessageDescriptor } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { type SearchJiraProductType } from '@atlassian/search-client/common/types';
import { jiraIssueTypeJQLQuery } from '@atlassian/search-client/issue';
import {
	type ConfluenceRangeFilter,
	type SearchConfluenceDocumentStatus,
	type SearchContainerStatus,
	type SearchIssueStatusCategory,
} from '@atlassian/search-client/query-generator';

import {
	type AllFilterStates,
	CUSTOM_OPTION_VALUE,
} from '../../../../common/constants/filters/types';
import { TypeFilterValueKey } from '../../../../common/constants/filters/universal-type';
import { type PrimaryProductKey, ProductKeys } from '../../../../common/constants/product-keys';
import {
	convertCustomDateToRange,
	convertLastUpdatedToDateRange,
	convertSortToFilter,
	getConfluenceDateOptionFromCustomDates,
	getConfluenceDateOptionValues,
} from '../../../../common/utils/filters/date-sort-utils';
import { isSelectFilter } from '../../base-select-filter/utils';

import { messages } from './messages';

type SearchStatusKeys = 'showCurrent' | 'showArchived';

export const ContainerStatusKeys: {
	[K in SearchStatusKeys]: SearchContainerStatus[];
} = {
	['showCurrent']: [],
	['showArchived']: ['ARCHIVED', 'CURRENT'],
};

export const ContentStatusKeys: {
	[K in SearchStatusKeys]: SearchConfluenceDocumentStatus[];
} = {
	['showCurrent']: [],
	['showArchived']: ['ARCHIVED', 'CURRENT', 'DRAFT'],
};

export const getContentStatusValue: (
	filter?: AllFilterStates,
) => SearchConfluenceDocumentStatus[] = (filter?: AllFilterStates) => {
	const archivedContentOption = getBooleanFilterValue(filter);

	return archivedContentOption
		? ContentStatusKeys['showArchived']
		: ContentStatusKeys['showCurrent'];
};

export const getContainerStatusValue: (filter?: AllFilterStates) => SearchContainerStatus[] = (
	filter?: AllFilterStates,
) => {
	const archivedContentOption = getBooleanFilterValue(filter);

	return archivedContentOption
		? ContainerStatusKeys['showArchived']
		: ContainerStatusKeys['showCurrent'];
};

export const getArchivedAndPendingComponentsValue: (filter?: AllFilterStates) => string[] = (
	filter?: AllFilterStates,
): string[] => {
	const archivedContentOption = getBooleanFilterValue(filter);

	return archivedContentOption ? ['ACTIVE', 'PENDING', 'ARCHIVED'] : [];
};

type JiraProductKeys =
	| typeof ProductKeys.Jira
	| typeof ProductKeys.JiraProductDiscovery
	| typeof ProductKeys.JiraServiceManagement;

export const JiraProductProjectFilterKey: {
	[K in JiraProductKeys]: SearchJiraProductType[];
} = {
	[ProductKeys.Jira]: ['software', 'business'],
	[ProductKeys.JiraProductDiscovery]: ['product_discovery'],
	[ProductKeys.JiraServiceManagement]: ['service_desk'],
};

export const omitNonJiraFilterValues: <T>(
	filterValues: T[],
	selectedProduct?: ProductKeys,
) => T[] = <T,>(filterValues: T[], selectedProduct?: ProductKeys): T[] => {
	// when all products are searched
	if (!selectedProduct) {
		// don't return any values in all product selected mode for assignee and project filters
		return [];
	}

	switch (selectedProduct) {
		case ProductKeys.Jira:
		case ProductKeys.JiraProductDiscovery:
		case ProductKeys.JiraServiceManagement:
			return filterValues;
	}

	return [];
};

// providing dummy value when no results returned so that it still filters on the type
// otherwise an empty array would return all types and not match what the user filters
export const DUMMY_JIRA_ISSUE_TYPE_ID = 'DUMMY_JIRA_ISSUE_TYPE_ID';

export const getJiraIssueTypesFilter: (
	cloudId: string | undefined,
	intl: IntlShape,
	issueType: Partial<TypeFilterValueKey>,
	activationId?: string,
	primaryProduct?: PrimaryProductKey,
) => Promise<string> = async (
	cloudId: string | undefined,
	intl: IntlShape,
	issueType: Partial<TypeFilterValueKey>,
	activationId?: string,
	primaryProduct?: PrimaryProductKey,
): Promise<string> => {
	if (!cloudId) {
		return DUMMY_JIRA_ISSUE_TYPE_ID;
	}
	const headers = buildAtlAttributionHeaders({
		cloudId,
		activationId,
		product: primaryProduct,
	});
	const searchString = intl.formatMessage(messages[issueType] as MessageDescriptor);
	const issueTypes = await jiraIssueTypeJQLQuery(cloudId, searchString, headers);

	const ids = issueTypes?.data?.jira.jqlBuilder.fieldValues.edges;
	if (!ids || ids.length === 0) {
		return DUMMY_JIRA_ISSUE_TYPE_ID;
	}
	if (ids.length > 1) {
		const issueTypeNode = ids.find((id) => id.node?.displayName === searchString);
		if (!issueTypeNode) {
			return DUMMY_JIRA_ISSUE_TYPE_ID;
		}
		const issueType = issueTypeNode.node?.issueTypes[0];
		return issueType && issueType.issueTypeId ? issueType.issueTypeId : DUMMY_JIRA_ISSUE_TYPE_ID;
	} else {
		const node = ids[0].node;
		if (!node) {
			return DUMMY_JIRA_ISSUE_TYPE_ID;
		}
		return node.issueTypes[0].issueTypeId || DUMMY_JIRA_ISSUE_TYPE_ID;
	}
};

export const getSelectFilterValue: (filter?: AllFilterStates) => string[] = (
	filter?: AllFilterStates,
): string[] => {
	return isSelectFilter(filter?.type) ? filter.value : [];
};
export const getSelectFilterCustomOptionValue: (
	filter?: AllFilterStates,
) => Record<string, string> | undefined = (filter?: AllFilterStates) => {
	return isSelectFilter(filter?.type) ? filter.customValue : {};
};

export const getBooleanFilterValue: (filter?: AllFilterStates) => boolean = (
	filter?: AllFilterStates,
): boolean => {
	return filter?.type === 'boolean' ? filter.value : false;
};

export const getSelectedDateRange: (filter?: AllFilterStates) =>
	| {
			gt: string | undefined;
			lt: string | undefined;
	  }
	| undefined = (filter?: AllFilterStates) => {
	const selectedDateOption = getSelectFilterValue(filter)[0];
	if (selectedDateOption && selectedDateOption !== CUSTOM_OPTION_VALUE) {
		return convertLastUpdatedToDateRange(selectedDateOption);
	} else if (selectedDateOption === CUSTOM_OPTION_VALUE) {
		const customDateRange = getSelectFilterCustomOptionValue(filter);
		if (customDateRange?.from || customDateRange?.to) {
			return convertCustomDateToRange(customDateRange.from, customDateRange.to);
		}
	}
	return;
};

export const getFilterDateRange: (filter?: AllFilterStates) => ConfluenceRangeFilter[] = (
	filter?: AllFilterStates,
): ConfluenceRangeFilter[] => {
	const selectedDateOption = getSelectFilterValue(filter)[0];

	if (selectedDateOption && selectedDateOption !== CUSTOM_OPTION_VALUE) {
		return [getConfluenceDateOptionValues(selectedDateOption)];
	} else if (selectedDateOption === CUSTOM_OPTION_VALUE) {
		const customDateRange = getSelectFilterCustomOptionValue(filter);
		if (customDateRange?.from && customDateRange?.to) {
			return [getConfluenceDateOptionFromCustomDates(customDateRange.from, customDateRange.to)];
		}
	}
	return [];
};

export const getJiraProjectFilter: (selectedProduct?: ProductKeys) => SearchJiraProductType[] = (
	selectedProduct?: ProductKeys,
): SearchJiraProductType[] => {
	if (!selectedProduct) {
		return []; // In the BE, it will target all Jira products if the array is empty
	}

	switch (selectedProduct) {
		case ProductKeys.Jira:
		case ProductKeys.JiraProductDiscovery:
		case ProductKeys.JiraServiceManagement:
			return JiraProductProjectFilterKey[selectedProduct];
	}

	return [];
};

export const jiraWorkTypesForRequest: Partial<Record<TypeFilterValueKey, string>> = {
	[TypeFilterValueKey.Epic]: 'Epic',
	[TypeFilterValueKey.Bug]: 'Bug',
	[TypeFilterValueKey.Story]: 'Story',
	[TypeFilterValueKey.Task]: 'Task',
	[TypeFilterValueKey.Subtask]: 'Subtask',
};

export const getSelectedJiraWorkType: (
	selectedType: TypeFilterValueKey[],
	selectedProduct?: ProductKeys,
) => string[] = (selectedType: TypeFilterValueKey[], selectedProduct?: ProductKeys): string[] => {
	const hasSelectedProductThatIsNotJira =
		Boolean(selectedProduct) && selectedProduct !== ProductKeys.Jira;
	if (hasSelectedProductThatIsNotJira || selectedType.length === 0) {
		return [];
	}

	return selectedType.flatMap((typeOption) => {
		const workType = jiraWorkTypesForRequest[typeOption];
		return workType ? [workType] : [];
	});
};

// Only use the following values for Jira issue statuses or else it will be ignored in
// the GraphQL query request.
export const allowedJiraIssueStatuses: SearchIssueStatusCategory[] = [
	'OPEN',
	'NEW',
	'INDETERMINATE',
	'DONE',
	'UNDEFINED',
];

export const getJiraIssueStatusFilterOptions: (
	filter?: AllFilterStates,
) => SearchIssueStatusCategory[] = (filter?: AllFilterStates): SearchIssueStatusCategory[] => {
	const filterValues = getSelectFilterValue(filter);
	const appliedFilterOptions = filterValues.flatMap((filtervalue) => {
		const filterOptions = allowedJiraIssueStatuses
			.filter((status) => {
				if (!fg('rovo_search_in_jira_granular_status_filters')) {
					return status === 'OPEN' || status === 'DONE';
				} else {
					return status !== 'OPEN';
				}
			})
			.find((allowedValue) => filtervalue === allowedValue);
		return filterOptions ? [filterOptions] : [];
	});
	return appliedFilterOptions;
};

export const getSortFilters = (
	filter?: AllFilterStates,
):
	| {
			field: 'latestModifiedDate';
			order: 'DESC';
	  }[]
	| {
			field: 'latestModifiedDate';
			order: 'ASC';
	  }[]
	| {
			field: 'createdDate';
			order: 'DESC';
	  }[]
	| {
			field: 'createdDate';
			order: 'ASC';
	  }[] => {
	const selectedSortOption = getSelectFilterValue(filter)[0];
	return convertSortToFilter(selectedSortOption);
};
